import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { scoreColor } from "@/components/ScoreMeter";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, GitCompare, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const ROWS = [
  ["Overall Score", "overall_score", false],
  ["Demand", "demand", false],
  ["Competition", "competition", true],
  ["Monetization", "monetization", false],
  ["Build Difficulty", "build_difficulty", true],
  ["Trend Velocity", "trend_velocity", false],
  ["Customer Pain", "pain_level", false],
];

export default function Compare() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [opps, setOpps] = useState([]);
  const [rec, setRec] = useState(null);
  const [loadingRec, setLoadingRec] = useState(false);

  useEffect(() => {
    const slugs = (params.get("slugs") || "").split(",").filter(Boolean);
    Promise.all(slugs.map((s) => api.get(`/opportunities/${s}`).then((r) => r.data).catch(() => null)))
      .then((res) => setOpps(res.filter(Boolean)));
  }, [params]);

  const val = (o, key) => key === "overall_score" ? o.overall_score : (o.scores?.[key] ?? 0);

  const askAI = async () => {
    if (!user) { window.location.href = "/login"; return; }
    setLoadingRec(true);
    try {
      const summary = opps.map((o) => `${o.title} (overall ${o.overall_score}, demand ${o.scores.demand}, competition ${o.scores.competition}, monetization ${o.scores.monetization})`).join("; ");
      const res = await api.post("/ai/chat", { message: `If I were building one of these, which should I pick and why? Be concise. Opportunities: ${summary}` });
      setRec(res.data.reply);
    } catch (e) { toast.error(e.response?.data?.detail || "AI failed"); }
    finally { setLoadingRec(false); }
  };

  if (!opps.length) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/discover" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-cyan-300 mb-6"><ArrowLeft className="w-4 h-4" />Back to feed</Link>
      <h1 className="font-display text-3xl font-extrabold text-slate-50 flex items-center gap-3 mb-8"><GitCompare className="w-7 h-7 text-cyan-400" />Compare Opportunities</h1>

      <div className="glass rounded-2xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="compare-table">
          <thead>
            <tr className="border-b border-white/8">
              <th className="text-left p-4 text-slate-500 font-medium">Metric</th>
              {opps.map((o) => (
                <th key={o.slug} className="p-4 text-left min-w-[180px]">
                  <Link to={`/opportunities/${o.slug}`} className="font-semibold text-slate-100 hover:text-cyan-300">{o.title}</Link>
                  <p className="text-xs font-mono text-slate-500 mt-1">{o.pricing}</p>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map(([label, key, invert]) => {
              const values = opps.map((o) => val(o, key));
              const best = invert ? Math.min(...values) : Math.max(...values);
              return (
                <tr key={key} className="border-b border-white/5">
                  <td className="p-4 text-slate-400">{label}</td>
                  {opps.map((o) => {
                    const v = val(o, key);
                    const isBest = v === best;
                    return (
                      <td key={o.slug} className="p-4">
                        <span className="font-mono font-bold" style={{ color: scoreColor(invert ? 100 - v : v) }}>{v}</span>
                        {isBest && <span className="ml-2 text-[10px] font-mono uppercase text-emerald-400">Best</span>}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            <tr>
              <td className="p-4 text-slate-400">Revenue Potential</td>
              {opps.map((o) => <td key={o.slug} className="p-4 text-emerald-400 font-mono text-xs">{o.revenue_potential}</td>)}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="glass rounded-2xl p-6 mt-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl font-bold text-slate-50 flex items-center gap-2"><Sparkles className="w-5 h-5 text-cyan-400" />If I were building one…</h3>
          <Button onClick={askAI} disabled={loadingRec} className="bg-cyan-500 text-slate-950" data-testid="compare-ai-btn">
            {loadingRec ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ask Nova"}
          </Button>
        </div>
        {rec ? <p className="text-slate-300 whitespace-pre-wrap text-sm leading-relaxed">{rec}</p> : <p className="text-slate-500 text-sm">Get an AI recommendation on which opportunity to build first.</p>}
      </div>
    </div>
  );
}
